import { html, LitElement, PropertyValues } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { localized, msg, str } from '@lit/localize';
import styles from './attachments-panel.scss';
import { when } from "lit/directives/when.js";
import { map } from "lit/directives/map.js";
import type { ElementInternals } from "element-internals-polyfill/dist/element-internals";
import "../banner/usos-banner";
import "../navigation/usos-link";
import { getLocale } from "../locale";


/**
 * Type of response from server after uploading a file, containing necessary information about
 * uploaded file to display it in attachments panel and provide download link.
 */
type UploadedFile = {
    fileID: string,
    fileName: string,
    fileSize: number | null,
    mimeType: string,
    comment?: string,
}

/**
 * Type of error response from server with a message to be shown to user.
 */
type UploadError = {
    message: {
        pl: string;
        en: string;
    } | string;
}

/**
 * A form-associated web component providing a complete UI for managing file attachments.
 *
 * The component supports uploading multiple files, displaying previously uploaded files,
 * downloading single or all files, and removing files. All network operations are fully
 * configurable through URL attributes, allowing the component to be used in various modes
 * (upload-only, read-only, mixed).
 *
 * Uploaded files returned by the backend must follow the `UploadedFile` structure.
 * The component automatically updates its form value by exposing each uploaded file ID
 * under the name specified in the `name` attribute.
 *
 * File type and size constraints can be displayed to the user and partially enforced
 * on the client side. Additional POST parameters may be passed using `extra-post-params`,
 * which must be provided as a correctly encoded query-string fragment.
 *
 * Drag & drop is supported for uploading files. If no upload URL is provided, the entire
 * upload interface is hidden. Similarly, delete and download features are disabled when
 * their respective URLs are not set.
 */
@customElement('attachments-panel')
@localized()
export class AttachmentsPanel extends LitElement {
    static override shadowRootOptions: ShadowRootInit = {
        ...LitElement.shadowRootOptions,
        delegatesFocus: true,
    };

    static override styles = styles;

    // section: properties

    /**
     * URL for uploading files.
     */
    @property({attribute: 'file-upload-url'}) fileUploadURL = '';

    /**
     * URL for removing files. May contain `{{FILE_ID}}` placeholder, which will
     * be replaced with actual file ID when making delete request.
     */
    @property({attribute: 'file-delete-url'}) fileDeleteURL = '';

    /**
     * URL for downloading a single file, with `{{FILE_ID}}` placeholder for file ID.
     */
    @property({attribute: 'file-download-url'}) fileDownloadURL = '';

    /**
     * URL for downloading all files.
     */
    @property({attribute: 'download-all-files-url'}) downloadAllFilesURL = '';

    /**
     * Name of the parameter used for file upload in POST request. Default is "file".
     */
    @property({attribute: 'upload-file-param-name'}) uploadFileParamName = 'file';

    /**
     * Name of the parameter used for delete file in POST request. Default is "fileID".
     */
    @property({attribute: 'delete-file-param-name'}) deleteFileParamName = 'fileID';

    /**
     * Extra POST parameters to include in upload and delete requests, in
     * URL-encoded format (e.g. `param1=value1&param2=value2`).
     * Note: this is a simple string and the component does not do any
     * validation or encoding of it, so it should be properly encoded by the caller.
     */
    @property({attribute: 'extra-post-params'}) extraPostParams = '';

    /**
     * Name of the form parameter containing the uploaded file ID.
     * The parameter will be repeated for each uploaded file.
     */
    @property() name = '';

    /**
     * Comma-separated list of allowed file types for upload, each item must be a uppercase
     * file types (e.g. `PDF,JPEG`).
     * When accessed as a property, this is an array of strings.
     */
    @property({
        attribute: 'file-types',
        converter: {
            fromAttribute: (value: string) => value.split(',').map(v => v.trim().toUpperCase())
                .filter(v => AttachmentsPanel.mimeTypesMap[v] !== undefined),
            toAttribute: (value: string[]) => value.join(','),
        },
        reflect: false,
    }) fileTypes: string[] = [];

    /**
     * Minimum number of files that must be uploaded.
     * This is only used for displaying requirements to the user, the component does not enforce it.
     */
    @property({type: Number, attribute: 'min-files-count'}) minFilesCount: Optional<number> = null;

    /**
     * Maximum number of files that can be uploaded.
     * This is only used for displaying requirements to the user and disabling the upload button.
     */
    @property({type: Number, attribute: 'max-files-count'}) maxFilesCount: Optional<number> = null;

    /**
     * Maximum allowed file size in bytes. This is used for displaying requirements to the user and to
     * prevent uploading files that exceed this size, but it still should be also validated on the server side.
     */
    @property({type: Number, attribute: 'max-file-size'}) maxFileSize: Optional<number> = null;


    // section: state

    @state() private uploadedFiles: UploadedFile[] = [];
    @state() private errors: string[] = [];


    // section: extra fields

    static formAssociated = true;

    private _globalDragHandler = ((e: DragEvent) => e.preventDefault()).bind(this);
    private _internals: ElementInternals;

    private static mimeTypesMap: {[k: string]: string} = {
        'PDF': 'application/pdf',
        'PNG': 'image/png',
        'JPEG': 'image/jpeg',
        // add more mappings as needed
    };

    @query('input[type=file]') private _fileUploadInput!: HTMLInputElement;
    @query('#upload-button') private _uploadButton!: HTMLButtonElement;


    // section: lifecycle

    constructor() {
        super();
        this._internals = this.attachInternals();
        this.addEventListener('invalid', () => {
            this.errors = [this._internals.validationMessage];
        });
    }

    override connectedCallback() {
        super.connectedCallback();
        window.addEventListener("dragover", this._globalDragHandler);
        window.addEventListener("drop", this._globalDragHandler);
    }

    override disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener("dragover", this._globalDragHandler);
        window.removeEventListener("drop", this._globalDragHandler);
    }

    protected override updated(changedProperties: PropertyValues<this>): void {
        if (changedProperties.has('minFilesCount')) {
            this._updateValidity();
        }
    }


    // section render

    override render() {
        return html`
            ${this._renderErrors()}
            <div class="usos-ui">
                ${when(this.fileUploadURL != '', () => html`
                    <input type="file" multiple class="sr-only" tabindex="-1" @change=${this._onFileInputChange}>
                    <div id="upload">
                        <button id="upload-button" @click=${this._onUploadClick} @drop=${this._onFileDrop}>
                            <div>
                                ${msg("Wgraj pliki")} <usos-icon icon-name="outlined.file_upload"></usos-icon>
                                <span>${msg("lub upuść je tutaj")}</span>
                            </div>

                        </button>
                        ${this._renderRequirements()}
                    </div>
                `)}
                <div id="files" aria-live="polite">
                    ${this._renderFilesSection()}
                </div>
            </div>
        `;
    }

    private _renderErrors(){
        if (!this.errors.length) return '';
        return html`
            <usos-banner type="error">
                ${map(this.errors, err => html`<div>${err}</div>`)}
            </usos-banner>
        `
    }

    private _renderRequirements() {
        const reqs = [
            this.fileTypes.length == 1 ? `${msg("Obsługiwany format")}: ${this.fileTypes.join(', ')}.` : '',
            this.fileTypes.length > 1 ? `${msg("Obsługiwane formaty")}: ${this.fileTypes.join(', ')}.` : '',
            this.maxFileSize != null ? `${msg("Dopuszczalna wielkość pliku")}: ${this._formatSize(this.maxFileSize)}.` : '',
            this.minFilesCount != null ? `${msg("Minimalna liczba plików")}: ${this.minFilesCount}.` : '',
            this.maxFilesCount != null ? `${msg("Maksymalna liczba plików")}: ${this.maxFilesCount}.` : ''
        ].filter(Boolean);
        if (reqs.length == 0) return '';
        return html`
            <div id="upload-reqs">
                <ol>
                    ${map(reqs, r => html`<li>${r}</li>`)}
                </ol>
            </div>
        `
    }

    private _renderFilesSection() {
        if (this.uploadedFiles.length == 0) return '';

        return html`
            <ol>
                ${map(this.uploadedFiles, file => html`
                    <li>
                        <a href="${this._replaceFileIDInURL(this.fileDownloadURL, file.fileID)}">
                            <span class="sr-only">${msg("Pobierz plik")}</span>
                            ${file.fileName} ·
                            ${when(file.fileSize !== null, () => html`${this._formatSize(file.fileSize as number)} ·`)}
                            ${this._renderFileFormat(file.mimeType)}
                            <usos-icon icon-name="outlined.file_download"></usos-icon></a>
                        ${when(this.fileDeleteURL != '', () => html`
                            <usos-icon class="delete-button" icon-name="outlined.delete"
                                is-button label="${msg("Usuń plik")}"
                                @click=${() => this._deleteFile(file.fileID)}>
                            </usos-icon>
                        `)}
                        ${when(!!file.comment, () => html`
                            <div class="comment">
                                (${file.comment})
                            </div>
                        `)}
                    </li>`
                )}
            </ol>
            ${when(this.downloadAllFilesURL != '', () => html`
                <div id="download-all">
                    <usos-link icon-location="right" icon-name="outlined.file_download">
                        <a href="${this.downloadAllFilesURL}">
                            ${msg("Pobierz wszystkie")}
                        </a>
                    </usos-link>
                </div>
            `)}
        `;
    }

    private _renderFileFormat(mimeType: string) {
        for (const type in AttachmentsPanel.mimeTypesMap) {
            if (AttachmentsPanel.mimeTypesMap[type] === mimeType) {
                return type;
            }
        }
        return msg("(nieznany format)")
    }


    // section: extra methods

    /**
     * Set previously uploaded files
     * @param files
     */
    setInitialFiles(files: UploadedFile[]) {
        this._setUploadedFiles(files);
    }


    // section: event handlers

    private _onUploadClick(){
        this._fileUploadInput?.click();
    }

    private async _onFileDrop(evt: DragEvent) {
        evt.stopPropagation();
        evt.preventDefault();

        await this._handleFiles(evt.dataTransfer?.files);
    }

    private _verifyFileType(file: File) {
        const possibleMimeTypes = this.fileTypes
            .map(fileType => AttachmentsPanel.mimeTypesMap[fileType] || '');
        if (possibleMimeTypes.indexOf(file.type) == -1) {
            const formatsMessage = this.fileTypes.length == 1 ? `${msg("Dopuszczalny format to")}: ${this.fileTypes[0]}`:
                `${msg("Dopuszczalne formaty to")}: ${this.fileTypes.join(', ')}`;
            throw new Error(`${msg("Nieobsługiwany format pliku")}. ${formatsMessage}.`);
        }
    }

    private _verifyFileSize(file: File) {
        if (file.size > this.maxFileSize!) {
            throw new Error(`${msg("Plik jest za duży. Dopuszczalna wielkość to")}: ${this._formatSize(this.maxFileSize!)}.`);
        }
    }

    private async _uploadFile(file: File): Promise<UploadedFile> {
        if (this.fileTypes.length > 0) {
            this._verifyFileType(file);
        }
        if (this.maxFileSize != null) {
            this._verifyFileSize(file);
        }

        const formData = new FormData();
        this.extraPostParams.split('&').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value) {
                formData.append(key, value);
            }
        });
        formData.append(this.uploadFileParamName, file);
        const resp = await fetch(this.fileUploadURL, {
            method: 'POST',
            headers: {},
            body: formData,
        });
        if (!resp.ok) {
            let message = '';
            try {
                const errorContents = await resp.json() as UploadError;
                if (typeof errorContents.message === 'string')
                    message = errorContents.message;
                else {
                    message = errorContents.message[getLocale() as 'pl' | 'en'];
                }
            } catch {}
            throw new Error(message || msg("Nie udało się wgrać pliku."));
        }

        const fileData = await resp.json() as UploadedFile;
        return fileData;
    }

    private async _handleFiles(files: FileList | undefined) {
        if (!files || files.length == 0) return;
        this.errors = [];

        const fileDatas = await Promise.allSettled(
            Array.from(files).map(file => {
                return this._uploadFile(file);
            })
        );
        const newErrors: string[] = [];
        const newUploadedFiles = [...this.uploadedFiles];
        fileDatas.forEach((uploadResult, idx) => {
            if (uploadResult.status == 'rejected') {
                newErrors.push(`${files[idx].name}: ${(uploadResult.reason as Error).message}`);
            }
            if (uploadResult.status == 'fulfilled') {
                newUploadedFiles.push(uploadResult.value);
            }
        });

        const filesRes = this._setUploadedFiles(newUploadedFiles);
        if (filesRes != '') newErrors.push(filesRes);
        this.errors = newErrors;
    }

    private async _onFileInputChange() {
        await this._handleFiles(this._fileUploadInput.files || undefined);
    }

    private async _deleteFile(fileID: string){
        const formData = new FormData();
        if (this.deleteFileParamName != '') {
            formData.append(this.deleteFileParamName, fileID);
        }
        this.extraPostParams.split('&').forEach(param => {
            const [key, value] = param.split('=');
            if (key && value) {
                formData.append(key, value);
            }
        });
        const resp = await fetch(this._replaceFileIDInURL(this.fileDeleteURL, fileID), {
            method: 'POST',
            body: formData,
        });
        if (resp.ok) {
            this._setUploadedFiles(this.uploadedFiles.filter(file => file.fileID != fileID));
            this.errors = [];
        } else {
            this.errors = [msg("Usunięcie pliku nie powiodło się.")];
        }
    }


    // section: private methods

    /**
     * Updates uploaded files within component and component's form data.
     * Verifies components maxFilesCount and returns error message if exceeded.
     */
    private _setUploadedFiles(files: UploadedFile[]): string {
        let ret = '';
        if (this.maxFilesCount != null && files.length > this.maxFilesCount) {
            const slicedFileNames = files.slice(this.maxFilesCount)
                                    .map(file => file.fileName).join(', ');
            files = files.slice(0, this.maxFilesCount);
            ret = `${msg("Maksymalna liczba plików")}: ${this.maxFilesCount}. ${msg("Nie wgrano")}: ${slicedFileNames}.`
        }

        this.uploadedFiles = files;
        // update form value for each file
        const formData = new FormData();
        this.uploadedFiles.forEach(file => {
            formData.append(this.name, file.fileID);
        });
        this._internals.setFormValue(formData);
        this._updateValidity();

        return ret;
    }

    private _formatSize(size: number){
        const units = ['B', 'kB', 'MB'];
        let i;
        const one = (s: number) => Math.round(s * 10) / 10;
        for (i = 0; i < units.length - 1; i++) {
            if (size >= 1024) size = Math.round(size) / 1024;
            else return `${one(size)} ${units[i]}`;
        }
        return `${one(size)} ${units[i]}`;
    }

    /** Replaces {{FILE_ID}} with provided fileID */
    private _replaceFileIDInURL(url: string, fileID: string): string {
        return url.replace(/(\{|%7B){2}FILE_ID(%7D|\}){2}/g, fileID);
    }

    private _updateValidity() {
        const isValid = this.uploadedFiles.length >= (this.minFilesCount ?? 0);
        this._internals.setValidity(
            isValid ? {} : {customError: true},
            isValid ? '' : msg(str`Wymagana liczba plików to ${this.minFilesCount}. Wgrano ${this.uploadedFiles.length}. Dodaj kolejne pliki.`),
            this._uploadButton
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'attachments-panel': AttachmentsPanel;
    }
}
